/**
 * Reading citations ("CRC 3.110(b)", "CCP 1010.6; 1011; 1013", "LR 2.11(D)(2)", "Fee Schedule item 5") and finding the
 * document that holds each rule or section they name, from rule-index.json (scripts/build-content.py). No DOM, so
 * scripts/action-provisions.mjs reads citations the way the app does.
 */
const SEC = String.raw`\d+(?:\.\d+)*[a-z]?`;
export const SUB = String.raw`(?:\([0-9A-Za-z]{1,4}\))+`;
const ITEM = String.raw`${SEC}(?:${SUB})?(?:-(?:${SEC})?(?:${SUB})?)?(?:,\s*${SUB}(?:-${SUB})?)*`;
const CODES = ["CRC", "CCP", "GC", "LR", "Fee Schedule item"];
const CODE = `(?:${CODES.join("|")})`;
// A citation: a code and its numbers, going on through "; 3.401", ", 3.770", " and 2.551" or "; CCP 417.30".
export const CITATION = new RegExp(String.raw`\b${CODE} ${ITEM}(?:(?:,\s*|;\s*|\s+and\s+)(?:${CODE} )?${ITEM})*`, "g");

/**
 * The rules one citation names: [{ code, number, to, subs }], where `to` ends a range ("CRC 2.100-2.119") and
 * `subs` are the subdivisions cited ("(b)" of "CRC 3.110(b)", "(b)(1)" of "CCP 431.30(b)(1)", "(C)" and "(D)" of
 * "LR 3.5(C),(D)").
 */
export function parseCitation(text) {
  const refs = [];
  let code = null;
  for (const raw of text.split(/;\s*|,\s*|\s+and\s+/)) {
    let part = raw.trim();
    const m = part.match(new RegExp(`^(${CODES.join("|")}) (.*)$`));
    if (m) [, code, part] = m;
    if (/^\(/.test(part) && refs.length) { refs.at(-1).subs.push(part.match(new RegExp(`^${SUB}`))[0]); continue; }
    const s = part.match(new RegExp(String.raw`^(${SEC})(${SUB})?(?:-(${SEC})?(?:${SUB})?)?$`));
    if (!s || !code) continue;
    refs.push({ code, number: s[1], to: s[3] || null, subs: s[2] ? [s[2]] : [] });
  }
  return refs;
}

/** Every rule the citations in `text` name, as parseCitation gives them. */
export const citationsIn = (text) => [...String(text || "").matchAll(CITATION)].flatMap((m) => parseCitation(m[0]));

/**
 * The id of the document holding `number` of `code` in court `courtId`, or null. `index` is rule-index.json: for a
 * code, runs of [first integer part, document id] and the sections split across documents; for "LR", each court's
 * local rules; for the fee schedule, its document.
 */
export function documentIdFor(index, code, number, courtId) {
  const x = index[code];
  if (!x) return null;
  if (code === "LR") return x[courtId] || null;
  if (typeof x === "string") return x;
  if (x.split[number]) return x.split[number];
  const n = parseInt(number, 10);
  const run = x.runs.filter(([start]) => start <= n).at(-1);
  return run ? run[1] : null;
}
